import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Building2, ShieldAlert, Loader2, LogOut, LayoutDashboard, BarChart3,
  Store, GitBranch, Monitor, MessageSquare, Users, UserSearch, Megaphone,
  Wallet, CreditCard, Tag, Bell, ShieldCheck, FileSearch, ChevronDown, Menu, X,
  PanelLeftClose, PanelLeftOpen, Mail, Activity, UserCog, Eye, KeyRound } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import PlatformLogin from './PlatformLogin';
import SystemHealthWidget from '../components/SystemHealthWidget';
import { PlatformPharmacies, PlatformPharmacyDetail } from './PlatformPharmacies';
import { PlatformKyc, PlatformCustomers, PlatformLeads, PlatformAudit } from './PlatformDesks';
import { PlatformAnalytics, PlatformDunning } from './PlatformPhase2';
import { PlatformBranchRequests, PlatformRequestDesk } from './PlatformOps';
import { PlatformPricing } from './PlatformPricing';
import { PlatformPlans } from './PlatformPlans';
import { PlatformRevenue, RevenueCard } from './PlatformRevenue';
import { PlatformInstallations, PlatformInstallationDetail } from './PlatformInstallations';
import { PlatformPatients } from './PlatformPatients';
import { PlatformMessaging } from './PlatformMessaging';
import { PlatformHealth } from './PlatformHealth';
import { PlatformTeam } from './PlatformTeam';
import { PlatformAccessContext } from './platformAccess';
import { PlatformPasswordChange } from './PlatformPasswordChange';
import { Toaster } from '../components/ui/sonner';
import './console.css';

export const Pill = ({ value }) => (
  <span className={`pc-pill pc-pill-${value || 'pending'}`}>{(value || 'pending').replace('_', ' ')}</span>
);

export const money = (n) => `GHS ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

const SIDEBAR_SECTIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { slug: '', label: 'Overview' },
      { slug: 'analytics', label: 'Analytics', icon: BarChart3 },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: Store,
    items: [
      { slug: 'pharmacies', label: 'Pharmacies', icon: Store },
      { slug: 'branch-requests', label: 'Branch approvals', icon: GitBranch },
      { slug: 'installations', label: 'Installations', icon: Monitor },
      { slug: 'requests', label: 'Pharmacy requests', icon: MessageSquare },
    ],
  },
  {
    key: 'people',
    label: 'People',
    icon: Users,
    items: [
      { slug: 'customers', label: 'Customers', icon: Users },
      { slug: 'patients', label: 'Patients', icon: UserSearch },
      { slug: 'leads', label: 'Leads', icon: Megaphone },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: Wallet,
    items: [
      { slug: 'revenue', label: 'Revenue', icon: Wallet },
      { slug: 'pricing', label: 'Pricing', icon: CreditCard },
      { slug: 'plans', label: 'Plans', icon: Tag },
      { slug: 'dunning', label: 'Renewals', icon: Bell },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    icon: ShieldCheck,
    items: [
      { slug: 'kyc', label: 'KYC review', icon: FileSearch },
      { slug: 'audit', label: 'Audit', icon: ShieldCheck },
      { slug: 'messaging', label: 'SMS & Email', icon: Mail },
      { slug: 'health', label: 'App health', icon: Activity },
    ],
  },
  {
    key: 'admin',
    label: 'Administration',
    icon: UserCog,
    items: [
      { slug: 'team', label: 'Team & roles', icon: UserCog },
    ],
  },
];

const sectionKey = (slug) => slug || 'overview';
const levelFor = (access, slug) => (access?.owner ? 'manage' : access?.access?.[sectionKey(slug)] || 'none');

const visibleSections = (access) => SIDEBAR_SECTIONS
  .map((s) => ({ ...s, items: s.items.filter((i) => (i.ownerOnly ? access?.owner : levelFor(access, i.slug) !== 'none')) }))
  .filter((s) => s.items.length);

const Guard = ({ access, slug, children }) => {
  const level = levelFor(access, slug);
  if (level === 'none') {
    return <div className="pc-card pc-noaccess" data-testid="pc-no-access"><ShieldAlert className="h-5 w-5" /><p>Your role ({access?.role_name}) does not include this section. Ask the platform owner if you need it.</p></div>;
  }
  if (level !== 'view') return children;
  return <div className="pc-readonly"><p className="pc-viewonly" data-testid="pc-view-only"><Eye className="h-3.5 w-3.5" /> View only — your role can see this section but not change it.</p>{children}</div>;
};

/* Determine which section contains the current route */
const sectionForSlug = (slug) => {
  for (const s of SIDEBAR_SECTIONS) {
    if (s.items.some((i) => i.slug === slug)) return s.key;
  }
  return 'dashboard';
};

const SevereReactionAlert = () => {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get('/platform/adr/severe-clusters').then(r => setRows(r.data.clusters || [])).catch(() => setRows([])); }, []);
  if (!rows.length) return null;
  const startRecall = (c) => navigate(`patients?${new URLSearchParams({ recall_product: c.medicine_name || '', recall_batch: c.batch_number || '', open: 'sms' })}`);
  return (
    <div className="pc-card" role="alert" style={{ background: '#3a0d0d', borderColor: '#b91c1c', marginBottom: '1rem', padding: '0 1rem 1rem' }} data-testid="pc-severe-reaction-banner">
      <div className="pc-card-h"><h2 style={{ color: '#fecaca' }}>Severe reaction signals</h2></div>
      {rows.map((c) => (
        <p key={`${c.tenant_id}-${c.cluster_key}`} style={{ color: '#fee2e2', margin: '.35rem 0' }} data-testid={`pc-severe-reaction-${c.tenant_id}-${c.cluster_key}`}>
          <b>{c.count} severe reactions</b> · {c.medicine_name} batch <span className="pc-mono">{c.batch_number}</span> · {c.pharmacy} · latest {when(c.latest_at)}
          <button type="button" className="pc-btn pc-btn-primary" style={{ marginLeft: '.75rem' }} onClick={() => startRecall(c)}
            data-testid={`pc-severe-reaction-recall-${c.tenant_id}-${c.cluster_key.replace(/[^a-z0-9-]/gi, "-")}`}>Start recall</button>
        </p>
      ))}
      <p className="pc-muted" style={{ marginTop: '.5rem' }}>Clears once the pharmacy's pharmacist marks it reviewed. Use Patients → Recall trace to text patients and lock the batch.</p>
    </div>
  );
};

const Overview = ({ owner, can }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/platform/overview').then(r => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <div className="pc-muted" data-testid="pc-overview-loading"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const p = data.pharmacies || {};
  return (
    <div data-testid="pc-overview">
      <h1 className="pc-h1">Platform overview</h1>
      <p className="pc-sub">Every pharmacy on DutiLyne · {data.year_month}</p>
      <SevereReactionAlert />
      <div className="pc-grid">
        {[
          ['Pharmacies', p.total],
          ['Trial', p.trial],
          ['Active', p.active],
          ['Past due', p.past_due],
          ['Suspended', p.suspended],
          ...(can('revenue') ? [['MRR', money(data.mrr_ghs)], ['Collected this month', money(data.revenue_month_ghs)]] : []),
        ].map(([label, value]) => (
          <div className="pc-tile" key={label} data-testid={`pc-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <b>{value ?? 0}</b><span>{label}</span>
          </div>
        ))}
      </div>
      <div className="pc-split">
        {can('revenue') && <RevenueCard />}
        {owner && <div className="pc-health-slot"><SystemHealthWidget /></div>}
        {can('revenue') && <div className="pc-card">
          <div className="pc-card-h"><h2>Add-on consumption this month</h2></div>
          <table className="pc-table">
            <thead><tr><th>Add-on</th><th className="pc-num">Units used across all pharmacies</th></tr></thead>
            <tbody>
              {Object.entries(data.addon_usage_month || {}).map(([kind, units]) => (
                <tr key={kind} data-testid={`pc-addon-total-${kind}`}>
                  <td style={{ textTransform: 'capitalize' }}>{kind}</td>
                  <td className="pc-num">{Number(units).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
        <div className="pc-card">
          <div className="pc-card-h"><h2>Waiting on you</h2></div>
          <table className="pc-table">
            <tbody>
              {can('kyc') && <tr data-testid="pc-kyc-waiting">
                <td>KYC documents pending</td>
                <td className="pc-num">{data.kyc_pending}</td>
                <td className="pc-num"><Link className="pc-btn" to="/platform/kyc">Review</Link></td>
              </tr>}
              {can('leads') && <tr data-testid="pc-leads-waiting">
                <td>New demo requests</td>
                <td className="pc-num">{data.leads_new}</td>
                <td className="pc-num"><Link className="pc-btn" to="/platform/leads">Open inbox</Link></td>
              </tr>}
              <tr>
                <td>Past-due pharmacies</td>
                <td className="pc-num">{p.past_due}</td>
                <td className="pc-num">{can('pharmacies') && <Link className="pc-btn" to="/platform/pharmacies?status=past_due">Chase</Link>}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const COLLAPSE_KEY = 'pc_sidebar_collapsed';

/* ── Sidebar Section Component ───────────────────────────────────── */
const SidebarSection = ({ section, openKeys, toggle, collapsed, onNavigate }) => {
  const isOpen = openKeys.includes(section.key);
  const Icon = section.icon;

  if (collapsed) {
    return (
      <div className="pcs-section pcs-section-rail" data-testid={`pc-sidebar-section-${section.key}`}>
        <div className="pcs-rail-group">
          <button
            type="button"
            className="pcs-rail-icon"
            title={section.label}
            data-testid={`pc-sidebar-toggle-${section.key}`}
          >
            <Icon className="pcs-icon" />
          </button>
          <div className="pcs-rail-flyout">
            <span className="pcs-rail-flyout-label">{section.label}</span>
            {section.items.map((item) => (
              <NavLink
                key={item.slug || '__overview'}
                to={`/platform/${item.slug}`}
                onClick={onNavigate}
                end={item.slug === ''}
                className={({ isActive }) => `pcs-link ${isActive ? 'pcs-link-active' : ''}`}
                data-testid={`pc-nav-${item.slug || 'overview'}`}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pcs-section" data-testid={`pc-sidebar-section-${section.key}`}>
      <button
        type="button"
        className={`pcs-trigger ${isOpen ? 'pcs-trigger-open' : ''}`}
        onClick={() => toggle(section.key)}
        data-testid={`pc-sidebar-toggle-${section.key}`}
      >
        <Icon className="pcs-icon" />
        <span className="pcs-trigger-label">{section.label}</span>
        <ChevronDown className={`pcs-chevron ${isOpen ? 'pcs-chevron-open' : ''}`} />
      </button>
      <div className={`pcs-items ${isOpen ? 'pcs-items-open' : ''}`}>
        {section.items.map((item) => (
          <NavLink
            key={item.slug || '__overview'}
            to={`/platform/${item.slug}`}
            onClick={onNavigate}
            end={item.slug === ''}
            className={({ isActive }) => `pcs-link ${isActive ? 'pcs-link-active' : ''}`}
            data-testid={`pc-nav-${item.slug || 'overview'}`}
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

export default function PlatformConsole() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [access, setAccess] = useState(null);
  const loadAccess = useCallback(() => api.get('/platform/access/me').then((r) => setAccess(r.data)).catch(() => setAccess(false)), []);
  useEffect(() => { if (user) loadAccess(); else setAccess(null); }, [user, loadAccess]);
  const isOwner = !!access;

  const toggleCollapse = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* */ }
      return next;
    });
  }, []);

  /* Determine which sidebar section(s) to open based on current route */
  const currentSlug = location.pathname.replace('/platform/', '').replace('/platform', '').split('/')[0];
  const activeSection = sectionForSlug(currentSlug);

  const [openKeys, setOpenKeys] = useState([activeSection]);

  /* Keep the active section open when the route changes */
  useEffect(() => {
    setOpenKeys((prev) => (prev.includes(activeSection) ? prev : [...prev, activeSection]));
  }, [activeSection]);

  /* Close mobile sidebar on navigation */
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  /* ESC key closes the mobile sidebar */
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileOpen]);

  const toggle = useCallback((key) => {
    setOpenKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  useEffect(() => {
    // Pharmacy accounts never get a platform session; stay on the platform sign-in.
    if (loading || !user || access !== false) return;
    logout();
  }, [loading, user, access, logout]);

  if (loading || (user && access === null)) {
    return (
      <div className="pc-shell pc-login-shell" data-testid="pc-loading">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: '#15a6a6' }} />
      </div>
    );
  }

  if (!user) return <PlatformLogin />;

  if (!isOwner) {
    return (
      <div className="pc-shell pc-login-shell" data-testid="pc-denied">
        <div style={{ textAlign: 'center' }}>
          <ShieldAlert className="h-8 w-8" style={{ margin: '0 auto .75rem', color: '#ff9b9b' }} />
          <h1 className="pc-h1">DutiLyne staff only</h1>
          <p className="pc-sub">Taking you back to the pharmacy app…</p>
        </div>
      </div>
    );
  }

  if (access.must_change_password) {
    return <><Toaster position="top-right" richColors /><PlatformPasswordChange onDone={loadAccess} onCancel={logout} /></>;
  }

  const g = (slug, el) => <Guard access={access} slug={slug}>{el}</Guard>;

  return (
    <PlatformAccessContext.Provider value={access}>
    <div className="pc-shell pc-shell-sidebar">
      <Toaster position="top-right" richColors />
      <header className="pc-top">
        <button
          type="button"
          className="pcs-mobile-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="pc-sidebar-mobile-toggle"
        >
          {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
        <div className="pc-brand"><Building2 className="h-4 w-4" style={{ color: '#15a6a6' }} />
          DutiLyne <small>Pharmacy POS · platform console</small>
        </div>
        <form className="pc-search" onSubmit={(e) => { e.preventDefault(); navigate(`/platform/pharmacies?search=${encodeURIComponent(search)}`); }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pharmacies by name, town or phone…"
            data-testid="pc-global-search"
          />
        </form>
        <span className="pc-muted" data-testid="pc-actor">{user?.name} · <span data-testid="pc-actor-role">{access.role_name}</span></span>
        <Link className="pc-btn" to="/platform/account" onClick={() => setMobileOpen(false)} title="My password" aria-label="My password" data-testid="pc-account-password-link"><KeyRound className="h-3.5 w-3.5" /></Link>
        <button type="button" className="pc-btn" onClick={logout} data-testid="pc-logout">
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </header>

      <div className="pc-layout">
        {/* Mobile overlay backdrop */}
        {mobileOpen && <div className="pcs-backdrop" onClick={() => setMobileOpen(false)} />}

        <aside className={`pc-sidebar ${mobileOpen ? 'pc-sidebar-open' : ''} ${collapsed ? 'pc-sidebar-collapsed' : ''}`} data-testid="pc-sidebar">
          <NavLink className="pcs-trigger" to="/platform/account" onClick={() => setMobileOpen(false)} title="My password" aria-label="My password" data-testid="pc-nav-account">
            <KeyRound className="pcs-icon" />{(!collapsed || mobileOpen) && <span className="pcs-trigger-label">My password</span>}
          </NavLink>
          {visibleSections(access).map((section) => (
            <SidebarSection key={section.key} section={section} openKeys={openKeys} toggle={toggle} collapsed={collapsed && !mobileOpen} onNavigate={() => setMobileOpen(false)} />
          ))}
          <button
            type="button"
            className="pcs-collapse-btn"
            onClick={toggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="pc-sidebar-collapse-toggle"
          >
            {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
          </button>
        </aside>

        <main className="pc-body">
          <Routes>
            <Route path="account" element={<PlatformPasswordChange forced={false} email={user.email} onCancel={() => navigate('/platform')} />} />
            <Route index element={g('', <Overview owner={access.owner} can={(k) => levelFor(access, k) !== 'none'} />)} />
            <Route path="installations" element={g('installations', <PlatformInstallations />)} />
            <Route path="installations/:tenantId" element={g('installations', <PlatformInstallationDetail />)} />
            <Route path="pharmacies" element={g('pharmacies', <PlatformPharmacies />)} />
            <Route path="pharmacies/:tenantId" element={g('pharmacies', <PlatformPharmacyDetail />)} />
            <Route path="branch-requests" element={g('branch-requests', <PlatformBranchRequests />)} />
            <Route path="requests" element={g('requests', <PlatformRequestDesk />)} />
            <Route path="pricing" element={g('pricing', <PlatformPricing />)} />
            <Route path="plans" element={g('plans', <PlatformPlans />)} />
            <Route path="revenue" element={g('revenue', <PlatformRevenue />)} />
            <Route path="analytics" element={g('analytics', <PlatformAnalytics />)} />
            <Route path="dunning" element={g('dunning', <PlatformDunning />)} />
            <Route path="kyc" element={g('kyc', <PlatformKyc />)} />
            <Route path="customers" element={g('customers', <PlatformCustomers />)} />
            <Route path="patients" element={g('patients', <PlatformPatients />)} />
            <Route path="leads" element={g('leads', <PlatformLeads />)} />
            <Route path="audit" element={g('audit', <PlatformAudit />)} />
            <Route path="messaging" element={g('messaging', <PlatformMessaging />)} />
            <Route path="health" element={g('health', <PlatformHealth />)} />
            <Route path="team" element={g('team', <PlatformTeam />)} />
            <Route path="*" element={<Navigate to="/platform" replace />} />
          </Routes>
        </main>
      </div>
    </div>
    </PlatformAccessContext.Provider>
  );
}
